import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { payrollEngineApi } from '../../../services/api';
import { Spinner, Modal, EmptyState, ConfirmModal } from '../../../components/shared/index';

const CATEGORIES = [
  { value: 'pre_tax_401k',          label: '401(k) Pre-tax',          group: 'pre_tax' },
  { value: 'pre_tax_401k_roth',     label: '401(k) Roth',             group: 'pre_tax' },
  { value: 'pre_tax_hsa',           label: 'HSA',                     group: 'pre_tax' },
  { value: 'pre_tax_fsa',           label: 'FSA',                     group: 'pre_tax' },
  { value: 'pre_tax_commuter',      label: 'Commuter',                group: 'pre_tax' },
  { value: 'pre_tax_health',        label: 'Health insurance',        group: 'pre_tax' },
  { value: 'pre_tax_dental',        label: 'Dental insurance',        group: 'pre_tax' },
  { value: 'pre_tax_vision',        label: 'Vision insurance',        group: 'pre_tax' },
  { value: 'post_tax_benefit',      label: 'Post-tax benefit',        group: 'post_tax' },
  { value: 'post_tax_garnishment',  label: 'Garnishment (creditor)',  group: 'post_tax' },
  { value: 'post_tax_child_support',label: 'Child support',           group: 'post_tax' },
  { value: 'post_tax_irs_levy',     label: 'IRS levy',                group: 'post_tax' },
  { value: 'post_tax_other',        label: 'Other post-tax',          group: 'post_tax' },
];

const PRIORITY_LABELS = {
  1: '1 · IRS levy',
  2: '2 · Child support',
  3: '3 · Creditor',
};

const CATEGORY_MAP = Object.fromEntries(CATEGORIES.map(c => [c.value, c]));

const EMPTY_FORM = {
  category: 'pre_tax_401k',
  name: '',
  amount_type: 'percent',
  amount: '',
  employer_contribution: '',
  employer_match_pct: '',
  employer_match_cap_pct: '',
  garnishment_priority: 1,
  annual_cap: '',
  effective_from: '',
  effective_to: '',
};

function fmtMoney(n) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(n) || 0);
}

function toNumOrNull(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function DeductionForm({ initial, onSubmit, onClose, pending }) {
  const [form, setForm] = useState(initial || EMPTY_FORM);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const cat = CATEGORY_MAP[form.category];
  const isRetirement  = form.category === 'pre_tax_401k' || form.category === 'pre_tax_401k_roth';
  const isGarnishment = form.category === 'post_tax_garnishment'
    || form.category === 'post_tax_child_support'
    || form.category === 'post_tax_irs_levy';

  const handle = (e) => {
    e.preventDefault();
    onSubmit({
      category: form.category,
      name: form.name,
      amount_type: form.amount_type,
      amount: toNumOrNull(form.amount) ?? 0,
      employer_contribution: toNumOrNull(form.employer_contribution),
      employer_match_pct:    isRetirement ? toNumOrNull(form.employer_match_pct) : null,
      employer_match_cap_pct: isRetirement ? toNumOrNull(form.employer_match_cap_pct) : null,
      garnishment_priority:  isGarnishment ? Number(form.garnishment_priority) : null,
      annual_cap: toNumOrNull(form.annual_cap),
      effective_from: form.effective_from || null,
      effective_to:   form.effective_to   || null,
    });
  };

  return (
    <form onSubmit={handle} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="label">Category *</label>
          <select
            className="input"
            value={form.category}
            onChange={(e) => set('category', e.target.value)}
            required
          >
            <optgroup label="Pre-tax">
              {CATEGORIES.filter(c => c.group === 'pre_tax').map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </optgroup>
            <optgroup label="Post-tax">
              {CATEGORIES.filter(c => c.group === 'post_tax').map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </optgroup>
          </select>
        </div>

        <div>
          <label className="label">Name *</label>
          <input
            className="input"
            required
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder={cat?.label || ''}
          />
        </div>

        <div>
          <label className="label">Amount type *</label>
          <select
            className="input"
            value={form.amount_type}
            onChange={(e) => set('amount_type', e.target.value)}
          >
            <option value="percent">Percent of gross</option>
            <option value="fixed">Fixed amount per pay</option>
          </select>
        </div>

        <div>
          <label className="label">
            Amount * {form.amount_type === 'percent' ? '(%)' : '($)'}
          </label>
          <input
            type="number" step="0.01" min="0"
            className="input"
            required
            value={form.amount}
            onChange={(e) => set('amount', e.target.value)}
          />
        </div>

        {isRetirement && (
          <>
            <div>
              <label className="label">Employer match %</label>
              <input
                type="number" step="0.01" min="0"
                className="input"
                value={form.employer_match_pct}
                onChange={(e) => set('employer_match_pct', e.target.value)}
                placeholder="e.g. 100"
              />
            </div>
            <div>
              <label className="label">Match cap % of salary</label>
              <input
                type="number" step="0.01" min="0"
                className="input"
                value={form.employer_match_cap_pct}
                onChange={(e) => set('employer_match_cap_pct', e.target.value)}
                placeholder="e.g. 4"
              />
            </div>
          </>
        )}

        {!isRetirement && (
          <div>
            <label className="label">Employer contribution $ (per pay)</label>
            <input
              type="number" step="0.01" min="0"
              className="input"
              value={form.employer_contribution}
              onChange={(e) => set('employer_contribution', e.target.value)}
            />
          </div>
        )}

        {isGarnishment && (
          <div>
            <label className="label">Garnishment priority *</label>
            <select
              className="input"
              value={form.garnishment_priority}
              onChange={(e) => set('garnishment_priority', e.target.value)}
            >
              <option value={1}>{PRIORITY_LABELS[1]}</option>
              <option value={2}>{PRIORITY_LABELS[2]}</option>
              <option value={3}>{PRIORITY_LABELS[3]}</option>
            </select>
          </div>
        )}

        <div>
          <label className="label">Annual cap $</label>
          <input
            type="number" step="0.01" min="0"
            className="input"
            value={form.annual_cap}
            onChange={(e) => set('annual_cap', e.target.value)}
            placeholder="Optional (e.g. 23000 for 401k)"
          />
        </div>

        <div>
          <label className="label">Effective from</label>
          <input
            type="date"
            className="input"
            value={form.effective_from}
            onChange={(e) => set('effective_from', e.target.value)}
          />
        </div>

        <div>
          <label className="label">Effective to (optional)</label>
          <input
            type="date"
            className="input"
            value={form.effective_to}
            onChange={(e) => set('effective_to', e.target.value)}
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
        <button
          type="submit"
          className={clsx('btn-primary', pending && 'opacity-50')}
          disabled={pending}
        >
          {pending ? 'Saving…' : initial ? 'Update deduction' : 'Add deduction'}
        </button>
      </div>
    </form>
  );
}

function DeductionRow({ d, onEdit, onDelete, canEdit }) {
  const cat = CATEGORY_MAP[d.category];
  const amountDisplay = d.amount_type === 'percent'
    ? `${Number(d.amount).toFixed(2)}%`
    : fmtMoney(d.amount);

  return (
    <li className="flex items-center justify-between px-4 py-3 hover:bg-gray-50">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900">{d.name}</span>
          <span className="inline-flex px-2 py-0.5 rounded-full text-xs border border-gray-200 bg-gray-50 text-gray-600">
            {cat?.label || d.category}
          </span>
          {d.garnishment_priority && (
            <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-amber-50 border border-amber-200 text-amber-700">
              Priority {PRIORITY_LABELS[d.garnishment_priority] || d.garnishment_priority}
            </span>
          )}
        </div>
        <div className="text-xs text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
          <span>{amountDisplay}</span>
          {d.annual_cap && <span>Cap {fmtMoney(d.annual_cap)}/yr</span>}
          {d.employer_match_pct != null && (
            <span>Match {Number(d.employer_match_pct).toFixed(1)}% up to {Number(d.employer_match_cap_pct || 0).toFixed(1)}%</span>
          )}
          {d.employer_contribution != null && d.employer_contribution !== 0 && (
            <span>Employer {fmtMoney(d.employer_contribution)}/pay</span>
          )}
          {d.effective_from && <span>From {new Date(d.effective_from).toLocaleDateString()}</span>}
          {d.effective_to && <span>To {new Date(d.effective_to).toLocaleDateString()}</span>}
        </div>
      </div>
      {canEdit && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => onEdit(d)}
            className="p-1.5 rounded hover:bg-blue-50 text-blue-600"
            title="Edit"
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(d)}
            className="p-1.5 rounded hover:bg-red-50 text-red-600"
            title="Archive"
          >
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      )}
    </li>
  );
}

export default function DeductionsList({ employeeId, canEdit = true }) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen]     = useState(false);
  const [editing, setEditing]     = useState(null);
  const [confirm, setConfirm]     = useState(null);

  const { data: deductions = [], isLoading } = useQuery({
    queryKey: ['deductions', employeeId],
    queryFn:  () => payrollEngineApi.listDeductions(employeeId).then(r => r.data || []),
    enabled:  !!employeeId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['deductions', employeeId] });

  const add = useMutation({
    mutationFn: (data) => payrollEngineApi.addDeduction(employeeId, data),
    onSuccess: () => { toast.success('Deduction added'); setAddOpen(false); invalidate(); },
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to add deduction'),
  });

  const update = useMutation({
    mutationFn: ({ id, data }) => payrollEngineApi.updateDeduction(id, data),
    onSuccess: () => { toast.success('Deduction updated'); setEditing(null); invalidate(); },
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to update deduction'),
  });

  const remove = useMutation({
    mutationFn: (id) => payrollEngineApi.deleteDeduction(id),
    onSuccess: () => { toast.success('Deduction archived'); invalidate(); },
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to archive deduction'),
  });

  const grouped = useMemo(() => {
    const pre = [];
    const post = [];
    for (const d of deductions) {
      const cat = CATEGORY_MAP[d.category];
      if (cat?.group === 'post_tax' || String(d.category).startsWith('post_tax')) post.push(d);
      else pre.push(d);
    }
    return { pre, post };
  }, [deductions]);

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  const hasAny = deductions.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="section-title">Ongoing deductions</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Applied on every pay run until the effective-to date.
          </p>
        </div>
        {canEdit && (
          <button onClick={() => setAddOpen(true)} className="btn-primary">
            <PlusIcon className="w-4 h-4" /> Add deduction
          </button>
        )}
      </div>

      {!hasAny ? (
        <EmptyState
          title="No deductions yet"
          description="Add 401(k) contributions, health premiums, HSA, garnishments, and more."
          action={canEdit ? (
            <button onClick={() => setAddOpen(true)} className="btn-primary">
              <PlusIcon className="w-4 h-4" /> Add deduction
            </button>
          ) : null}
        />
      ) : (
        <div className="space-y-5">
          <div className="card p-0 overflow-hidden">
            <div className="px-4 py-2 bg-blue-50/50 border-b border-blue-100">
              <span className="text-xs font-semibold uppercase tracking-wide text-blue-700">
                Pre-tax ({grouped.pre.length})
              </span>
            </div>
            {grouped.pre.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-400 text-center">No pre-tax deductions.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {grouped.pre.map(d => (
                  <DeductionRow
                    key={d.id}
                    d={d}
                    onEdit={setEditing}
                    onDelete={setConfirm}
                    canEdit={canEdit}
                  />
                ))}
              </ul>
            )}
          </div>

          <div className="card p-0 overflow-hidden">
            <div className="px-4 py-2 bg-amber-50/60 border-b border-amber-100">
              <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                Post-tax ({grouped.post.length})
              </span>
            </div>
            {grouped.post.length === 0 ? (
              <p className="px-4 py-6 text-sm text-gray-400 text-center">No post-tax deductions.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {grouped.post.map(d => (
                  <DeductionRow
                    key={d.id}
                    d={d}
                    onEdit={setEditing}
                    onDelete={setConfirm}
                    canEdit={canEdit}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add deduction" size="lg">
        <DeductionForm
          onSubmit={(data) => add.mutate(data)}
          onClose={() => setAddOpen(false)}
          pending={add.isPending}
        />
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit deduction" size="lg">
        {editing && (
          <DeductionForm
            initial={{
              category: editing.category,
              name: editing.name || '',
              amount_type: editing.amount_type || 'percent',
              amount: editing.amount ?? '',
              employer_contribution: editing.employer_contribution ?? '',
              employer_match_pct: editing.employer_match_pct ?? '',
              employer_match_cap_pct: editing.employer_match_cap_pct ?? '',
              garnishment_priority: editing.garnishment_priority ?? 1,
              annual_cap: editing.annual_cap ?? '',
              effective_from: editing.effective_from ? editing.effective_from.substring(0, 10) : '',
              effective_to:   editing.effective_to   ? editing.effective_to.substring(0, 10)   : '',
            }}
            onSubmit={(data) => update.mutate({ id: editing.id, data })}
            onClose={() => setEditing(null)}
            pending={update.isPending}
          />
        )}
      </Modal>

      <ConfirmModal
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => remove.mutate(confirm.id)}
        title="Archive this deduction?"
        message={`"${confirm?.name}" will stop applying to future pay runs. Historical pay stubs are preserved.`}
        danger
      />
    </div>
  );
}
